import React, { useCallback, useContext, useEffect, useState } from "react";
import { VerbBuildStatus, WorkspaceStatus } from "models/Workspace.model";
import { getVerbBuildConfigFromWorkspace } from "./utils/index";
import Spinner from "components/UI-lib/Spinner";
import FlatCard from "components/UI-lib/FlatCard";
import { WaitingOnBuildStep } from "../NodeDetail/Environment/DeprecatedSettings/InstanceSettings/WaitingOnBuildStep";
import { VERB_FAILED_PHRASE, VERB_FINISHED_PHRASE } from "utils/verb";
import { WorkspaceCreateContext } from "contexts/WorkspaceCreateContext";

import { Loader1 } from "components/FullScreenLoader";
import { Environment } from "components/Environment/utils/types";
import {
  getEnvironmentCloudCredID,
  getEnvironmentStatus,
  getVerbBuildStatusFromTasks,
} from "components/Environment/utils/environtmentUtils";
import { LogViewer } from "components/LogViewer";

interface IVerbBuildComponentProps {
  workspaceId: string;
  setShowButton: (b: boolean) => void;
  environment: Environment | null;
  initialVerbLoad: boolean;
  showEmptyState?: boolean;
}
export const VerbBuildComponent: React.FC<IVerbBuildComponentProps> = ({
  workspaceId,
  setShowButton,
  environment,
  initialVerbLoad,
  showEmptyState = false,
}) => {
  // const [jupyterUrl, setJupyterUrl] = useState("");
  const createContext = useContext(WorkspaceCreateContext);
  const {
    isVerbBuilding,
    logFileContent,
    setBuildButtonDisabled,
    setIsVerbBuilding,
    verbYaml,
  } = createContext;
  const [showAddNotebookForm, setShowAddNotebookForm] = useState(false);

  const verbBuild = getVerbBuildConfigFromWorkspace(
    environment?.build?.verb?.config || "",
  );
  const environmentCloudCredId = getEnvironmentCloudCredID(environment);
  const isAkashEnvironment = environmentCloudCredId === "akash-brev-wg";
  const canAddNotebooks = Boolean(
    environment &&
    getEnvironmentStatus(environment) === WorkspaceStatus.Running &&
    getVerbBuildStatusFromTasks(environment.tasks || []) ===
      VerbBuildStatus.Completed,
  );

  useEffect(() => {
    if (logFileContent.includes(VERB_FINISHED_PHRASE)) {
      setIsVerbBuilding(false);
      // if (!createContext.logFileContent.includes(SHAKA)) {
      //   createContext.setLogFileContent(createContext.logFileContent + SHAKA);
      // }
    }
  }, [logFileContent, setIsVerbBuilding]);

  useEffect(() => {
    if (
      environment &&
      !(
        logFileContent.includes(VERB_FAILED_PHRASE) ||
        getVerbBuildStatusFromTasks(environment?.tasks || []) ===
          VerbBuildStatus.CreateFailed
      ) &&
      (verbYaml === "" ||
        isVerbBuilding === true ||
        getEnvironmentStatus(environment) === WorkspaceStatus.Deleting ||
        getEnvironmentStatus(environment) === WorkspaceStatus.Stopping ||
        getEnvironmentStatus(environment) === WorkspaceStatus.Stopped)
    ) {
      setBuildButtonDisabled(true);
    } else {
      setBuildButtonDisabled(false);
    }
  }, [
    isVerbBuilding,
    logFileContent,
    setBuildButtonDisabled,
    verbYaml,
    environment,
  ]);

  const getTitle = useCallback(() => {
    const verbStatus = getVerbBuildStatusFromTasks(environment?.tasks || []);

    if (verbStatus === VerbBuildStatus.Completed) {
      return "Build Completed 🤙";
    } else if (verbStatus === VerbBuildStatus.CreateFailed) {
      return "Build Failed";
    } else {
      return "Building...";
    }
  }, [environment?.tasks]);

  const determineContentToRender = () => {
    // console.log(`
    // -> hitBuild ${createContext.hitBuildButton}
    // -> createContext.logFileContent==="" ${createContext.logFileContent === ""}
    // -> workspace?.status ${workspace?.status}
    // -> workspace?.verbBuildStatus ${workspace?.verbBuildStatus}
    // `);

    if (
      createContext.hitBuildButton &&
      createContext.logFileContent === "" &&
      environment &&
      getVerbBuildStatusFromTasks(environment?.tasks || []) !==
        VerbBuildStatus.CreateFailed &&
      !isAkashEnvironment
    ) {
      return (
        <>
          <Spinner type="secondary" />
          <WaitingOnBuildStep
            onComplete={() => {
              setShowButton(true);
            }}
          />
        </>
      );
    } else if (
      environment &&
      (getEnvironmentStatus(environment) === WorkspaceStatus.Starting ||
        getEnvironmentStatus(environment) === WorkspaceStatus.Deploying) &&
      getVerbBuildStatusFromTasks(environment?.tasks || []) ===
        VerbBuildStatus.Pending &&
      createContext.logFileContent === ""
    ) {
      return (
        <>
          <FlatCard className="mt-2 mb-2" isLoading={false}>
            <p className="text-sm text-gray-700 dark:text-secondary font-mono">
              Logs will appear here when the container starts building.
              <span className="text-gray-700 dark:text-slate-300">
                {" Please wait until the instance is in Running state."}
              </span>
            </p>
          </FlatCard>
        </>
      );
    } else if (
      environment &&
      (getVerbBuildStatusFromTasks(environment?.tasks || []) ===
        VerbBuildStatus.Building ||
        getVerbBuildStatusFromTasks(environment?.tasks || []) ===
          VerbBuildStatus.Completed ||
        getVerbBuildStatusFromTasks(environment?.tasks || []) ===
          VerbBuildStatus.Pending ||
        getVerbBuildStatusFromTasks(environment?.tasks || []) ===
          VerbBuildStatus.CreateFailed) &&
      createContext.logFileContent !== ""
    ) {
      return (
        <>
          <LogViewer
            logs={createContext.logFileContent}
            didSucceed={
              getVerbBuildStatusFromTasks(environment?.tasks || []) ===
              VerbBuildStatus.Completed
            }
            didFail={
              getVerbBuildStatusFromTasks(environment?.tasks || []) ===
              VerbBuildStatus.CreateFailed
            }
            title={getTitle()}
          />
        </>
      );
    }
  };

  if (
    environment &&
    getEnvironmentStatus(environment) !== WorkspaceStatus.Running &&
    createContext.logFileContent === ""
  ) {
    return (
      <div className="flex w-full">
        <FlatCard isLoading={false}>
          <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
            Logs will appear here when the instance is running.
          </p>
        </FlatCard>
      </div>
    );
  }

  const getContainerBuildMessage = () => {
    if (environment?.build?.vm) {
      return "Building VM Mode";
    }

    if (
      !!verbBuild?.base_image ||
      environment?.build?.container?.base_image ||
      environment?.build?.docker_compose
    ) {
      const containerType =
        verbBuild?.base_image ??
        environment?.build?.container?.base_image ??
        (environment?.build?.docker_compose ? "Docker Compose" : "") ??
        "Unknown Container";

      return `Building ${containerType}`;
    }

    return "Python and CUDA will be installed once the container is ready";
  };

  return (
    <div>
      <div className="dark:text-secondary">
        <div className="flex flex-col">
          {environment &&
            ((getEnvironmentStatus(environment) === WorkspaceStatus.Running &&
              getVerbBuildStatusFromTasks(environment?.tasks || []) ===
                VerbBuildStatus.Building) ||
              (!isAkashEnvironment &&
                getEnvironmentStatus(environment) !==
                  WorkspaceStatus.Starting &&
                getEnvironmentStatus(environment) !==
                  WorkspaceStatus.Deploying &&
                getEnvironmentStatus(environment) !== WorkspaceStatus.Stopped &&
                getEnvironmentStatus(environment) !==
                  WorkspaceStatus.Stopping &&
                getVerbBuildStatusFromTasks(environment?.tasks || []) !==
                  VerbBuildStatus.Completed &&
                !createContext.logFileContent.includes(VERB_FINISHED_PHRASE) &&
                !createContext.logFileContent.includes(VERB_FAILED_PHRASE) &&
                getVerbBuildStatusFromTasks(environment?.tasks || []) !==
                  VerbBuildStatus.CreateFailed)) && (
              <FlatCard className="mt-3" isLoading={false}>
                <p className="text-sm text-amber-700 dark:text-amber-500 font-mono text-center">
                  {getContainerBuildMessage()}
                </p>
              </FlatCard>
            )}

          {environment &&
            isAkashEnvironment &&
            getVerbBuildStatusFromTasks(environment?.tasks || []) ===
              VerbBuildStatus.Completed &&
            getEnvironmentStatus(environment) === WorkspaceStatus.Running && (
              <FlatCard className="mt-3" isLoading={false}>
                <p className="text-sm text-amber-700 dark:text-amber-500 font-mono">
                  Container running on instance.
                </p>
              </FlatCard>
            )}

          {environment &&
            (createContext.logFileContent.includes(VERB_FAILED_PHRASE) ||
              getVerbBuildStatusFromTasks(environment?.tasks || []) ===
                VerbBuildStatus.CreateFailed) &&
            getEnvironmentStatus(environment) === WorkspaceStatus.Running && (
              <FlatCard className="mt-3" isLoading={false}>
                <p className="text-sm text-amber-700 dark:text-amber-500 font-mono text-center">
                  The container failed to build. Please reach out to support or{" "}
                  {environment.build?.container?.base_image !== ""
                    ? "try setting up the container manually in VM mode"
                    : "rebuild it"}
                </p>
              </FlatCard>
            )}

          {environment &&
            (getEnvironmentStatus(environment) === WorkspaceStatus.Running ||
              getEnvironmentStatus(environment) === WorkspaceStatus.Starting ||
              getEnvironmentStatus(environment) ===
                WorkspaceStatus.Deploying) &&
            initialVerbLoad &&
            createContext.logFileContent === "" &&
            !createContext.hitBuildButton && (
              <FlatCard className="mt-3" isLoading={true}>
                <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
                  Checking for Container Logs...
                </p>
              </FlatCard>
            )}
          {showEmptyState &&
            environment &&
            getEnvironmentStatus(environment) === WorkspaceStatus.Running &&
            getVerbBuildStatusFromTasks(environment.tasks || []) ===
              VerbBuildStatus.Completed &&
            !initialVerbLoad &&
            createContext.logFileContent === "" && (
              <FlatCard className="mt-3" isLoading>
                <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
                  Loading build logs...
                </p>
              </FlatCard>
            )}
          {!showEmptyState &&
            environment &&
            getEnvironmentStatus(environment) === WorkspaceStatus.Running &&
            getVerbBuildStatusFromTasks(environment.tasks || []) ===
              VerbBuildStatus.Completed &&
            !initialVerbLoad &&
            createContext.logFileContent === "" && (
              <FlatCard className="mt-3" isLoading>
                <p className="text-sm text-gray-700 dark:text-secondary font-mono text-center">
                  Loading build logs...
                </p>
              </FlatCard>
            )}
          {environment &&
          (getEnvironmentStatus(environment) === WorkspaceStatus.Deploying ||
            getEnvironmentStatus(environment) === WorkspaceStatus.Starting) &&
          getVerbBuildStatusFromTasks(environment?.tasks || []) !==
            VerbBuildStatus.Completed &&
          getVerbBuildStatusFromTasks(environment?.tasks || []) !==
            VerbBuildStatus.CreateFailed &&
          createContext.logFileContent === "" ? (
            <FlatCard className="mt-3" isLoading={false}>
              <p className="text-sm text-amber-700 dark:text-amber-500 font-mono">
                Waiting for your instance to be running before{" "}
                {!!verbBuild?.base_image
                  ? `building ${verbBuild.base_image}`
                  : "installing Python and CUDA"}
                <span>
                  <Loader1 />
                </span>
              </p>
            </FlatCard>
          ) : (
            <>
              <div>{determineContentToRender()}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
